import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Users, FileText, MessageSquare, BarChart3 } from "lucide-react";

const Admin = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError("You don't have admin access");
      } else {
        setError("Failed to load admin stats");
      }
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="bg-destructive/10 border border-destructive/30 rounded-md p-6 text-center">
            <p className="text-destructive font-medium">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2" data-testid="admin-title">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Platform overview and statistics
          </p>
        </div>

        {loading ? (
          <div className="bento-grid">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 skeleton" />
            ))}
          </div>
        ) : stats && (
          <>
            {/* Stats Cards */}
            <div className="bento-grid mb-8">
              <div className="bg-card border border-border rounded-md p-6" data-testid="stat-users">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 text-primary flex items-center justify-center">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-semibold">{stats.total_users}</p>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-md p-6" data-testid="stat-contracts">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 text-primary flex items-center justify-center">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-semibold">{stats.total_contracts}</p>
                    <p className="text-sm text-muted-foreground">Contracts Uploaded</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-md p-6" data-testid="stat-questions">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 text-primary flex items-center justify-center">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-semibold">{stats.total_questions}</p>
                    <p className="text-sm text-muted-foreground">Questions Asked</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Question Categories */}
            <div className="bg-card border border-border rounded-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h2 className="font-medium">Questions by Category</h2>
              </div>
              <div className="space-y-3">
                {Object.entries(stats.questions_by_category).map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{category}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{
                            width: `${stats.total_questions > 0 ? (count / stats.total_questions) * 100 : 0}%`
                          }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Admin;
